import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Infinity as InfinityIcon, Cpu, Heart, Zap, Shield, Award, ArrowRight, ChevronDown, CircleAlert as AlertCircle, Calendar, User, BookOpen, Activity } from 'lucide-react';
import { formatPrice } from '../lib/currency';
import { useSupabaseNews } from '../hooks/useSupabaseNews';
import ProfileModal from '../components/ProfileModal';
import TankControlModal from '../components/TankControlModal';
import ParticleBackground from '../components/ParticleBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTankControlModal, setShowTankControlModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => {
    return localStorage.getItem('admin_logged_in') === 'true';
  });
  const { news } = useSupabaseNews();

  useEffect(() => {
    // Check admin status on component mount
    const adminStatus = localStorage.getItem('admin_logged_in');
    setIsAdmin(adminStatus === 'true');
  }, []);

  const sections = [
    {
      key: 'unlimited',
      title: t('landing.unlimited'),
      href: '/unlimited',
      gradient: 'from-red-700 to-[#EE1C23]',
      hoverGradient: 'from-red-600 to-[#EE1C23]',
      icon: <InfinityIcon className="w-12 h-12" />,
      description: t('landing.unlimitedDesc')
    },
    {
      key: 'robotics',
      title: t('landing.robotics'),
      href: '/robotics',
      gradient: 'from-red-600 to-[#EE1C23]',
      hoverGradient: 'from-[#EE1C23] to-red-600',
      icon: <Cpu className="w-12 h-12" />,
      description: t('landing.roboticsDesc')
    },
  ];

  // Debug info for production
  const debugInfo = {
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      <ParticleBackground />
      <LanguageSwitcher />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.08 }}
        className="text-center mb-16"
      >
        <div className="flex flex-col items-center mb-6">
          <div 
            id="logo-container"
            className="relative inline-block cursor-pointer transition-transform duration-100 hover:scale-105 active:scale-98 mb-4"
            onClick={async () => {
              const bullet = document.getElementById('bullet');
              const flash = document.getElementById('flash');
              const smoke = document.getElementById('smoke');
              const sound = document.getElementById('sound');
              const container = document.getElementById('logo-container');

              if (!bullet || !flash || !smoke || !sound || !container) return;

              // Remote tank command via Supabase Edge Function (HTTPS secure)
              // Fire without waiting for response to reduce delay
              fetch('https://kaxlblujljjnghknrdvb.supabase.co/functions/v1/tank-fire', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtheGxibHVqbGpqbmdoa25yZHZiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk4NTM3NTMsImV4cCI6MjA3NTQyOTc1M30.HbVQR49zzKLom2fONnoTvCNo27UDVGv8XPID7CJOx0Q`,
                  'Content-Type': 'application/json',
                },
              }).catch(error => {
                console.error('Tank fire error:', error);
              });

              // Restart animations
              bullet.classList.remove('shoot');
              flash.classList.remove('active');
              smoke.classList.remove('active');
              sound.classList.remove('active');
              container.classList.remove('shake');

              // Force reflow
              void bullet.offsetWidth;

              // Start effects
              bullet.classList.add('shoot');
              flash.classList.add('active');

              setTimeout(() => {
                smoke.classList.add('active');
              }, 20);

              container.classList.add('shake');
              sound.classList.add('active');
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.filter = "drop-shadow(0 0 30px rgba(238, 28, 35, 0.8))";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.filter = "drop-shadow(0 0 20px rgba(238, 28, 35, 0.5))";
            }}
            style={{
              filter: "drop-shadow(0 0 20px rgba(238, 28, 35, 0.5))"
            }}
          >
            <img
              src="https://res.cloudinary.com/dvq5iv4qs/image/upload/v1760482265/-ct_logo_light_copy_cxvyoy.png"
              alt="Cybertronic Logo"
              className="h-16 md:h-24"
            />
            
            {/* Bullet */}
            <div 
              id="bullet"
              className="absolute top-1/2 right-0 w-8 h-2 rounded transform -translate-y-1/2 hidden"
              style={{
                background: 'linear-gradient(90deg, #FFD700, #FFA500)',
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.8)'
              }}
            />
            
            {/* Flash */}
            <div 
              id="flash"
              className="absolute w-12 h-12 rounded-full top-1/2 pointer-events-none opacity-0"
              style={{
                background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,215,0,0.8) 30%, rgba(238,28,35,0.6) 70%, transparent 100%)',
                left: '20px',
                transform: 'translate(-50%, -50%)'
              }}
            />
            
            {/* Smoke */}
            <div 
              id="smoke"
              className="absolute w-5 h-5 rounded-full top-1/2 pointer-events-none opacity-0"
              style={{
                background: 'rgba(128, 128, 128, 0.6)',
                left: '20px',
                transform: 'translate(-50%, -50%)'
              }}
            />
            
            {/* Sound effect */}
            <div 
              id="sound"
              className="absolute top-5 left-1/2 transform -translate-x-1/2 text-yellow-400 text-2xl font-bold opacity-0 pointer-events-none font-orbitron"
            >
              BOOM!
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron leading-tight pb-2">
            {t('landing.title')}
          </h1>
        </div>
        <p className="text-lg text-gray-400 max-w-4xl mx-auto">
          <button 
            onClick={() => setShowProfileModal(true)}
            className="text-lg text-gray-400 hover:text-[#EE1C23] transition-colors cursor-pointer"
          >
            Ing. Letocha René
          </button>
        </p>
      </motion.div>

      {/* Mobile Sidebars - positioned above main content */}
      <div className="lg:hidden w-full max-w-6xl mb-8 px-4">
        <div className="flex flex-col space-y-4">
          {/* News first on mobile */}
          {news && news.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="bg-black/60 backdrop-blur-md border border-[#EE1C23]/30 rounded-2xl p-4"
            >
              <h3 className="text-lg font-bold text-white mb-3 font-orbitron flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-[#EE1C23]" />
                Novinky
              </h3>
              <div className="space-y-3">
                {news.slice(0, 2).map((article) => (
                  <div key={article.id} className="bg-gray-800/50 rounded-lg p-3">
                    <h4 className="text-white font-semibold text-sm mb-1 line-clamp-1">
                      {article.title?.[i18n.language] || article.title?.cs || 'Bez názvu'}
                    </h4>
                    <p className="text-gray-400 text-xs line-clamp-2">
                      {article.excerpt?.[i18n.language] || article.excerpt?.cs || 'Bez popisu'}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          
          {/* Tank Control second on mobile */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-black/60 backdrop-blur-md border border-[#EE1C23]/30 rounded-2xl p-4"
          >
            <h3 className="text-lg font-bold text-white mb-3 font-orbitron flex items-center">
              <Zap className="w-4 h-4 mr-2 text-[#EE1C23]" />
              {i18n.language === 'en' ? 'Tank Control' : 
               i18n.language === 'de' ? 'Panzer-Steuerung' : 
               i18n.language === 'sk' ? 'Ovládanie tanku' : 
               'Ovládání tanku'}
            </h3>
            <div className="space-y-3">
              <p className="text-gray-300 text-sm">
                {i18n.language === 'en' ? 'Get exclusive access to control our real tank remotely!' : 
                 i18n.language === 'de' ? 'Erhalten Sie exklusiven Zugang zur Fernsteuerung unseres echten Panzers!' : 
                 i18n.language === 'sk' ? 'Získajte exkluzívny prístup k ovládaniu nášho skutočného tanku na diaľku!' : 
                 'Získejte exkluzivní přístup k ovládání našeho skutečného tanku na dálku!'}
              </p>
              <div className="bg-gray-800/50 rounded-lg p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-white">
                    {i18n.language === 'en' ? '5 minutes control' : 
                     i18n.language === 'de' ? '5 Minuten Steuerung' : 
                     i18n.language === 'sk' ? '5 minút ovládania' : 
                     '5 minut ovládání'}
                  </span>
                  <span className="text-[#EE1C23] font-bold">{formatPrice(25, i18n.language)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-white">
                    {i18n.language === 'en' ? '15 minutes control' : 
                     i18n.language === 'de' ? '15 Minuten Steuerung' : 
                     i18n.language === 'sk' ? '15 minút ovládania' : 
                     '15 minut ovládání'}
                  </span>
                  <span className="text-[#EE1C23] font-bold">{formatPrice(60, i18n.language)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white">
                    {i18n.language === 'en' ? '30 minutes control' : 
                     i18n.language === 'de' ? '30 Minuten Steuerung' : 
                     i18n.language === 'sk' ? '30 minút ovládania' : 
                     '30 minut ovládání'}
                  </span>
                  <span className="text-[#EE1C23] font-bold">{formatPrice(100, i18n.language)}</span>
                </div>
              </div>
              <button
                onClick={() => setShowTankControlModal(true)}
                className="w-full bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-2 rounded-lg font-semibold text-sm"
              >
                {i18n.language === 'en' ? 'Control tank now' : 
                 i18n.language === 'de' ? 'Panzer jetzt steuern' : 
                 i18n.language === 'sk' ? 'Ovládať tank teraz' : 
                 'Ovládat tank nyní'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 max-w-6xl w-full">
        {sections.map((section, index) => (
          <motion.div
            key={section.key}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.06, delay: index * 0.02 }}
            whileHover={{ scale: 1.05, y: -10 }}
            whileTap={{ scale: 0.95 }}
            className="relative group cursor-pointer"
            onClick={() => {
              if (isAdmin) {
                navigate(`${section.href}?admin=true`);
              } else {
                navigate(section.href);
              }
             window.scrollTo(0, 0);
            }}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${section.gradient} rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
            <div className={`absolute inset-0 bg-gradient-to-br ${section.hoverGradient} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
            
            <div className={`relative bg-black/40 backdrop-blur-md border rounded-2xl p-8 h-80 flex flex-col justify-center items-center transition-colors duration-300 ${
              isAdmin 
                ? 'border-green-500/40 group-hover:border-green-400/60' 
                : 'border-red-500/20 group-hover:border-red-400/40'
            }`}>
              {section.badge && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.05 + index * 0.02 }}
                  className="absolute -top-3 -right-3 bg-gradient-to-r from-[#EE1C23] to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold"
                >
                  {section.badge}
                </motion.div>
              )}
              
              {isAdmin && (
                <div className="absolute top-4 left-4 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
                  ADMIN MODE
                </div>
              )}
              
              <div className="text-[#EE1C23] mb-6 group-hover:scale-110 transition-transform duration-300">
                {section.icon}
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center font-orbitron">
                {section.title}
              </h2>
              
              <p className="text-gray-300 text-center text-sm mb-6 px-4">
                {section.description}
              </p>
              
              <div className="w-16 h-1 bg-gradient-to-r from-[#EE1C23] to-red-600 rounded-full group-hover:w-24 transition-all duration-300"></div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        className="mt-16 text-center"
      >
        {isAdmin ? (
          <div className="space-y-3">
            <p className="text-green-400 text-sm font-bold">
              ADMIN REŽIM AKTIVNÍ
            </p>
            <p className="text-gray-500 text-xs">
              Klikněte na sekci pro správu produktů
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => navigate('/navody')}
                className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-semibold text-sm transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                Správa video návodů
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white rounded-lg font-semibold text-sm transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 flex items-center gap-2"
              >
                <Activity className="w-4 h-4" />
                Návštěvnost webu
              </button>
            </div>
            <button
              onClick={() => {
                setIsAdmin(false);
                localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_token');
              }}
              className="text-red-400 hover:text-red-300 text-xs underline"
            >
              Odhlásit se z admin režimu
            </button>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            {t('landing.selectSection')}
          </p>
        )}
      </motion.div>

      {/* News Sidebar - only show if there are news articles */}
      {news && news.length > 0 && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="fixed right-4 top-32 w-80 max-h-[50vh] z-40 hidden lg:block"
        >
          <div className="bg-black/60 backdrop-blur-md border border-[#EE1C23]/30 rounded-2xl p-6 max-h-full overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4 font-orbitron flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-[#EE1C23]" />
              Novinky
            </h3>
            <div className="space-y-4">
              {news.slice(0, 3).map((article) => (
                <div key={article.id} className="bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800/70 transition-colors cursor-pointer">
                  <div className="flex items-center text-gray-400 text-xs mb-2">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{new Date(article.date).toLocaleDateString('cs-CZ')}</span>
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-2 line-clamp-2">
                    {article.title?.[i18n.language] || article.title?.cs || 'Bez názvu'}
                  </h4>
                  <p className="text-gray-400 text-xs line-clamp-3">
                    {article.excerpt?.[i18n.language] || article.excerpt?.cs || 'Bez popisu'}
                  </p>
                </div>
              ))}
            </div>
            {news.length > 3 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    navigate('/news');
                    window.scrollTo(0, 0);
                  }}
                  className="text-[#EE1C23] hover:text-red-300 text-sm font-medium transition-colors"
                >
                  Zobrazit všechny →
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Tank Control Sidebar */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="fixed left-4 top-32 w-80 z-[45] hidden lg:block"
      >
        <div className="bg-black/60 backdrop-blur-md border border-[#EE1C23]/30 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 font-orbitron flex items-center">
            <Zap className="w-5 h-5 mr-2 text-[#EE1C23]" />
            {i18n.language === 'en' ? 'Tank Control' : 
             i18n.language === 'de' ? 'Panzer-Steuerung' : 
             i18n.language === 'sk' ? 'Ovládanie tanku' : 
             'Ovládání tanku'}
          </h3>
          <div className="space-y-4">
            <p className="text-gray-300 text-sm leading-relaxed">
              {i18n.language === 'en' ? 'Get exclusive access to control our real tank remotely!' : 
               i18n.language === 'de' ? 'Erhalten Sie exklusiven Zugang zur Fernsteuerung unseres echten Panzers!' : 
               i18n.language === 'sk' ? 'Získajte exkluzívny prístup k ovládaniu nášho skutočného tanku na diaľku!' : 
               'Získejte exkluzivní přístup k ovládání našeho skutečného tanku na dálku!'}
            </p>
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-semibold">
                  {i18n.language === 'en' ? '5 minutes control' : 
                   i18n.language === 'de' ? '5 Minuten Steuerung' : 
                   i18n.language === 'sk' ? '5 minút ovládania' : 
                   '5 minut ovládání'}
                </span>
                <span className="text-[#EE1C23] font-bold">{formatPrice(25, i18n.language)}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-semibold">
                  {i18n.language === 'en' ? '15 minutes control' : 
                   i18n.language === 'de' ? '15 Minuten Steuerung' : 
                   i18n.language === 'sk' ? '15 minút ovládania' : 
                   '15 minut ovládání'}
                </span>
                <span className="text-[#EE1C23] font-bold">{formatPrice(60, i18n.language)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">
                  {i18n.language === 'en' ? '30 minutes control' : 
                   i18n.language === 'de' ? '30 Minuten Steuerung' : 
                   i18n.language === 'sk' ? '30 minút ovládania' : 
                   '30 minut ovládání'}
                </span>
                <span className="text-[#EE1C23] font-bold">{formatPrice(100, i18n.language)}</span>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowTankControlModal(true)}
              className="w-full bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all flex items-center justify-center space-x-2"
            >
              <Zap className="w-4 h-4" />
              <span>
                {i18n.language === 'en' ? 'Control tank now' : 
                 i18n.language === 'de' ? 'Panzer jetzt steuern' : 
                 i18n.language === 'sk' ? 'Ovládať tank teraz' : 
                 'Ovládat tank nyní'}
              </span>
            </motion.button>
            <p className="text-gray-500 text-xs text-center">
              {i18n.language === 'en' ? 'Secure payments via Stripe' : 
               i18n.language === 'de' ? 'Sichere Zahlungen über Stripe' : 
               i18n.language === 'sk' ? 'Bezpečné platby cez Stripe' : 
               'Bezpečné platby přes Stripe'}
            </p>
          </div>
        </div>
      </motion.div>
      {showProfileModal && (
        <ProfileModal 
          onClose={() => setShowProfileModal(false)}
          onAdminLogin={() => {
            setIsAdmin(true);
            localStorage.setItem('admin_logged_in', 'true');
          }}
        />
      )}

      {showTankControlModal && (
        <TankControlModal 
          onClose={() => setShowTankControlModal(false)}
        />
      )}
    </div>
  );
}